"use client";

import { useCallback, useEffect, useState } from "react";

import log from "logbeacon";
import { useSnapshot } from "valtio";
import {
  PositionAssetTypeEnum,
  type PositionAsset,
} from "@/src/features/api/memberApi";
import { favoritesCenter, rootStore } from "@/src/store";

export interface UseUserFavoriteAssetsResult {
  /** 是否有任意资产（不区分市场） */
  hasAssets: boolean;
  /** 是否有美股资产（通过 asset.asset_market 包含 \"US\" && asset_type === STOCK 判断） */
  hasUSStocks: boolean;
  /** 是否有港股资产（通过 asset.asset_market 包含 \"HK\" && asset_type === STOCK 判断） */
  hasHKStocks: boolean;
  /** 是否有加密资产（asset_type === TOKEN） */
  hasCryptoAssets: boolean;
  isLoading: boolean;
  error: Error | null;
  folderId?: string;
  refresh: () => Promise<void>;
}

/**
 * 轻量级 Hook：
 * 1. 先确定用户是否已经有收藏资产（overall）
 * 2. 进一步按市场类型拆分：美股 / 港股 / 加密货币
 *
 * 调用方只需关心返回的 hasXXX 标志，不需要自己拉取 FavoritesCenter。
 */
export const useUserFavoriteAssets = (): UseUserFavoriteAssetsResult => {
  const { isLoggedIn } = useSnapshot(rootStore.login);

  const [hasAssets, setHasAssets] = useState(false);
  const [hasUSStocks, setHasUSStocks] = useState(false);
  const [hasHKStocks, setHasHKStocks] = useState(false);
  const [hasCryptoAssets, setHasCryptoAssets] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { ownerInfo } = useSnapshot(rootStore.user);
  const userId = ownerInfo?.id;
  const { userFolders } = useSnapshot(favoritesCenter.state);
  const folderId = userFolders?.[0]?.id || "";
  const assetCount = userFolders?.[0]?.asset_count ?? 0;

  const analyzeAssets = useCallback((rows: PositionAsset[] | undefined) => {
    if (!rows || rows.length === 0) {
      setHasAssets(false);
      setHasUSStocks(false);
      setHasHKStocks(false);
      setHasCryptoAssets(false);
      return;
    }

    setHasAssets(true);

    let us = false;
    let hk = false;
    let crypto = false;

    rows.forEach((item) => {
      const assetType = item.asset_type;
      const assetMarket = item.asset?.asset_market;

      if (assetType === PositionAssetTypeEnum.TOKEN) {
        crypto = true;
      }

      if (assetType === PositionAssetTypeEnum.STOCK) {
        if (assetMarket?.includes("US")) {
          us = true;
        } else if (assetMarket?.includes("HK")) {
          hk = true;
        }
      }
    });

    setHasUSStocks(us);
    setHasHKStocks(hk);
    setHasCryptoAssets(crypto);
  }, []);

  const fetchStatus = useCallback(async () => {
    if (
      !isLoggedIn ||
      !userId ||
      !userFolders ||
      userFolders.length === 0 ||
      !folderId
    ) {
      setHasAssets(false);
      setHasUSStocks(false);
      setHasHKStocks(false);
      setHasCryptoAssets(false);
      setError(new Error("Not logged in"));
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const contents = await favoritesCenter.fetchFolderContents(folderId, {
        // 这里只需要用来判断是否存在资产和类型，拿一页即可
        limit: 20,
        "enrich.with_asset": true,
        "enrich.enable": true,
        has_amount: false,
      });
      if (contents.success && Array.isArray(contents.data)) {
        analyzeAssets(contents.data as PositionAsset[]);
      } else {
        setHasAssets(false);
        setHasUSStocks(false);
        setHasHKStocks(false);
        setHasCryptoAssets(false);
        if (contents.error) {
          setError(new Error(contents.error));
        }
      }
    } catch (err) {
      log.error("[useUserFavoriteAssets] failed to fetch", err);
      setHasAssets(false);
      setHasUSStocks(false);
      setHasHKStocks(false);
      setHasCryptoAssets(false);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [isLoggedIn, userId, userFolders, folderId, analyzeAssets]);

  useEffect(() => {
    // 资产数量为 0，可以直接判定没有资产
    if (assetCount === 0) {
      setHasAssets(false);
      setHasUSStocks(false);
      setHasHKStocks(false);
      setHasCryptoAssets(false);
    }
  }, [assetCount]);

  useEffect(() => {
    if (isLoggedIn && !!userId && !!userFolders?.[0]?.id) {
      void fetchStatus();
    }
  }, [isLoggedIn, userId, userFolders, fetchStatus]);

  return {
    hasAssets,
    hasUSStocks,
    hasHKStocks,
    hasCryptoAssets,
    isLoading,
    error,
    folderId: userFolders?.[0]?.id || undefined,
    refresh: fetchStatus,
  };
};
